import React, { useEffect, useState } from "react";
import { api, fmtCurrency } from "../lib/api";
import { PageHeader, VendorTag } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkle, FilePdf, Trash, Plus, ArrowLeft, FileText, Lightning, PencilSimple, X, PaperPlaneTilt, Kanban } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_STYLE = {
  draft: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  sent: "text-[#0F1E38] bg-blue-500/10 border-blue-500/30",
  approved: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  awaiting_signature: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  signed: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30",
  accepted: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  rejected: "text-red-400 bg-red-500/10 border-red-500/30",
};

export default function Proposals() {
  const [proposals, setProposals] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState({});

  const load = async () => {
    const [p, i, c] = await Promise.all([api.get("/proposals"), api.get("/invitations"), api.get("/clients")]);
    setProposals(p.data); setInvitations(i.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c.company_name]));

  const generate = async (invToken) => {
    setGenerating((g) => ({ ...g, [invToken]: true }));
    try {
      const res = await api.post(`/proposals/generate/${invToken}`);
      toast.success("Proposal generated");
      await load();
      setSelected(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating((g) => ({ ...g, [invToken]: false }));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete proposal?")) return;
    await api.delete(`/proposals/${id}`); toast.success("Deleted"); load(); setSelected(null);
  };

  // Pending submissions that don't yet have a proposal
  const pendingSubmissions = invitations.filter(i => i.status === "submitted" && !i.proposal_id);

  if (selected) return <ProposalDetail proposal={selected} client={clients.find(c => c.id === selected.client_id)} onBack={() => { setSelected(null); load(); }} onDelete={() => del(selected.id)} onUpdate={setSelected} />;

  return (
    <div>
      <PageHeader
        overline="Proposals · PSDoc-owned Technical + CC Commercial"
        title="Proposals"
        subtitle="Auto-created when PSDoc completes HLD/LLD/SOW/BOM. Adjust BOM lines + commercial layer, then approve."
      />

      <div data-testid="proposals-info-banner" className="border border-blue-200 bg-blue-50 rounded-lg p-4 mb-6 text-sm text-slate-700">
        <b>New flow (Feb 2026):</b> Proposals are created automatically when PSDoc fires the <code className="text-xs bg-white px-1 rounded">sow_ready</code> webhook. No manual "Generate" step. Once a proposal appears here, you can adjust the BOM (qty, unit price, discounts, swap SKUs, add/delete lines) and set commercial terms — then Approve and issue the signing link.
      </div>

      {proposals.length === 0 && (
        <div className="grid-bg py-20 text-center text-sm text-slate-500 rounded border border-slate-200">
          <FileText size={28} className="mx-auto text-slate-400 mb-3" />
          No proposals yet. Once a client submits their intake and presales completes the SOW/BOM in PSDoc, a proposal will land here automatically.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {proposals.map((p) => (
          <div key={p.id} data-testid={`proposal-card-${p.id}`} className="tactical-card border border-slate-200 bg-white rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">{clientMap[p.client_id] || "—"}</div>
                <div className="font-display font-bold text-lg text-[#0F1E38] leading-tight mt-1">{p.title}</div>
              </div>
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${STATUS_STYLE[p.status]}`}>{p.status}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              <VendorTag vendor={p.intake?.vendor_preference} />
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-300 text-slate-500 uppercase">{p.intake?.topology}</span>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-300 text-slate-500">{p.intake?.num_sites} sites</span>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-baseline justify-between">
              <span className="font-mono font-bold text-2xl text-[#0F1E38]">{fmtCurrency((p.bom_totals?.grand_total) || (p.boq_totals?.grand_total))}</span>
              <button data-testid={`open-proposal-${p.id}`} onClick={() => setSelected(p)} className="text-xs font-mono uppercase text-[#0F1E38] hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}}>Open →</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProposalDetail({ proposal, client, onBack, onDelete, onUpdate }) {
  const [p, setP] = useState(proposal);
  const [editingBoq, setEditingBoq] = useState(false);
  // Signing link state — populated after Approve or on mount for approved proposals.
  // Shape: { signing_url, signing_token, signing_expires_at } | null
  const [signing, setSigning] = useState(null);

  // ── Data normalization (Feb 2026 refactor) ───────────────────────────────
  // New proposals use `bom_items` + `bom_totals` (from PSDoc sow_ready webhook).
  // Legacy proposals still have `boq_items` + `boq_totals`. Read from whichever
  // is populated; write ONLY the new keys.
  const bomSource = (p.bom_items && p.bom_items.length) ? p.bom_items : (p.boq_items || []);
  const totalsSource = (p.bom_totals && Object.keys(p.bom_totals).length) ? p.bom_totals : (p.boq_totals || {});
  const sowSource = (p.sow_sections && Object.keys(p.sow_sections).length) ? p.sow_sections : (p.psdoc_sections || {});
  // Normalize into internal state shape: {sku, description, qty, unit_price, discount_pct, vendor, source}
  const normalize = (arr) => (arr || []).map(it => ({
    sku:          it.sku || "",
    description:  it.description || "",
    qty:          Number(it.qty ?? it.quantity ?? 0),
    unit_price:   Number(it.unit_price ?? 0),
    discount_pct: Number(it.discount_pct ?? 0),
    vendor:       it.vendor || "",
    source:       it.source || (it.sku ? "psdoc" : "cc-added"),
  }));
  const [items, setItems] = useState(normalize(bomSource));
  // "Add to catalog" dialog — populated when admin clicks + on an unpriced BoM line.
  // Shape: { sku, description, vendor, list_price, targetIndex } | null
  const [catalogAdd, setCatalogAdd] = useState(null);
  const initCT = p.commercial_terms || { global_discount_pct: 0, global_tax_pct: 10, payment_terms: "Net 30", deposit_pct: 30, currency: "AUD", sales_notes: "", commercial_approver: "" };
  const [ct, setCt] = useState(initCT);
  // Locked once approved — BoM + commercial terms are immutable from here on;
  // every future change goes through a Change Order. UI reflects this by
  // disabling the Edit BoM button and status downgrade options.
  const isLocked = ["approved", "awaiting_signature", "signed"].includes(p.status);

  // Load / refresh the signing link from the linked project whenever the
  // proposal is in an approved-and-not-yet-signed state. This ensures the
  // link banner shows up even after a page refresh — no manual "Generate
  // Review Link" click required.
  useEffect(() => {
    let cancelled = false;
    const fetchSigning = async () => {
      if (!p.project_id) return;
      if (!["approved", "awaiting_signature"].includes(p.status)) return;
      try {
        const r = await api.get(`/projects/${p.project_id}`);
        const proj = r.data || {};
        if (cancelled) return;
        if (proj.signing_token && !proj.signed_at) {
          const origin = window.location.origin;
          setSigning({
            signing_url: `${origin}/sign/${proj.signing_token}`,
            signing_token: proj.signing_token,
            signing_expires_at: proj.signing_expires_at || "",
          });
        }
      } catch { /* silent — banner just won't render */ }
    };
    fetchSigning();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [p.id, p.status, p.project_id]);

  // Compute display totals from current items + commercial_terms — used for the
  // top-line grand total and the totals card, regardless of BOM edit state.
  const computeTotals = () => {
    const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0) * (1 - (Number(it.discount_pct) || 0) / 100), 0);
    const gd = subtotal * (Number(ct.global_discount_pct || 0) / 100);
    const afterGd = subtotal - gd;
    const tax = afterGd * (Number(ct.global_tax_pct || 0) / 100);
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      global_discount: Math.round(gd * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      grand_total: Math.round((afterGd + tax) * 100) / 100,
    };
  };
  const liveTotals = computeTotals();

  // Legacy SOW-polling removed — SOW now arrives fully-formed from PSDoc via sow_ready webhook.

  const updateItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addItem = () => setItems([...items, { description: "", sku: "", qty: 1, unit_price: 0, discount_pct: 0, vendor: "", source: "cc-added" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const saveBoq = async () => {
    try {
      const res = await api.put(`/proposals/${p.id}`, { bom_items: items, commercial_terms: ct });
      setP(res.data); setItems(normalize(res.data.bom_items || [])); setCt(res.data.commercial_terms || ct); onUpdate(res.data); setEditingBoq(false);
      toast.success("BOM + commercial terms saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const setStatus = async (status) => {
    try {
      const res = await api.put(`/proposals/${p.id}`, { status });
      setP(res.data); onUpdate(res.data);
      toast.success(`Marked ${status}`);
    } catch { toast.error("Failed"); }
  };

  const approveProposal = async () => {
    // Explicit warning — approval LOCKS the proposal. Any post-approval change
    // must flow through a Change Order (delta amendment), NOT a re-edit of the
    // original SOW/BOM/commercial terms.
    const bomLine = items.length > 0 ? `${items.length} BoM line(s) totalling ${fmtCurrency(liveTotals.grand_total)}` : "no BoM lines";
    const message =
      `⚠ APPROVING THIS PROPOSAL WILL LOCK IT.\n\n` +
      `You will NO LONGER be able to edit:\n` +
      `  • Cover letter\n` +
      `  • BoM (${bomLine})\n` +
      `  • Discounts + commercial terms\n\n` +
      `After approval, any customer-facing change must go through a Change Order (delta amendment attached to this proposal) — the signed SOW is never rewritten.\n\n` +
      `Approve now and advance the client to "Proposal Approved"?`;
    if (!window.confirm(message)) return;
    try {
      const r = await api.post(`/proposals/${p.id}/approve`);
      const fresh = await api.get(`/proposals/${p.id}`);
      setP(fresh.data); onUpdate(fresh.data);
      const url = r.data?.signing_url;
      if (url) {
        // Auto-mint the review link so admin can attach the SOW pack to
        // Outlook immediately — no separate "Generate Review Link" click.
        setSigning({
          signing_url: url,
          signing_token: r.data.signing_token || "",
          signing_expires_at: r.data.signing_expires_at || "",
        });
        try { await navigator.clipboard.writeText(url); } catch { /* clipboard may be blocked */ }
        toast.success(`Proposal approved — signing link copied. See the "Client Review Link" panel below.`, { duration: 8000 });
      } else {
        toast.success("Proposal approved — client status advanced");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const exportPDF = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked — enable popups for this site"); return; }
    const bomForPdf = items;
    const rows = bomForPdf.map((l, i) => {
      const net = (Number(l.qty) || 0) * (Number(l.unit_price) || 0) * (1 - (Number(l.discount_pct) || 0) / 100);
      return `
      <tr>
        <td style="padding:8px 10px;border-bottom:1px solid #eaeaea;">${i + 1}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #eaeaea;">${esc(l.description)}<div style="color:#888;font-size:10px;font-family:monospace;margin-top:2px;">${esc(l.sku || "")}</div></td>
        <td style="text-align:right;padding:8px 10px;border-bottom:1px solid #eaeaea;font-family:monospace;">${l.qty}</td>
        <td style="text-align:right;padding:8px 10px;border-bottom:1px solid #eaeaea;font-family:monospace;">$${(l.unit_price || 0).toFixed(2)}</td>
        <td style="text-align:right;padding:8px 10px;border-bottom:1px solid #eaeaea;font-family:monospace;">$${net.toFixed(2)}</td>
      </tr>`;
    }).join("");
    const sowHtml = "";

    const html = `<!doctype html><html><head><title>${esc(p.title)}</title>
      <style>
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111;padding:40px;max-width:900px;margin:auto;font-size:13px;line-height:1.5;}
        h1{font-size:28px;margin:0;letter-spacing:-0.02em;}
        h2{font-size:18px;margin-top:32px;border-bottom:1px solid #eaeaea;padding-bottom:6px;}
        h3{font-size:14px;margin-top:20px;}
        .meta{color:#666;font-size:12px;margin-top:6px;}
        table{width:100%;border-collapse:collapse;margin-top:16px;font-size:12px;}
        th{text-align:left;padding:8px 10px;background:#f4f4f5;font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:#555;}
        .totals{margin-top:20px;margin-left:auto;width:340px;font-size:13px;}
        .totals div{display:flex;justify-content:space-between;padding:6px 0;}
        .grand{border-top:2px solid #111;margin-top:6px;padding-top:10px;font-weight:700;font-size:18px;}
        .brand{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:16px;}
        .brand-r{text-align:right;font-size:11px;color:#666;}
        ul{padding-left:22px;} li{margin:4px 0;}
        .page-break{page-break-before:always;}
        code{background:#f4f4f5;padding:2px 4px;border-radius:3px;font-size:11px;}
        @media print { body { padding: 20px; } }
      </style></head><body>
      <div class="brand">
        <div>
          <h1>${esc(p.title)}</h1>
          <div class="meta">
            Proposal for: <strong>${esc(client?.company_name || "—")}</strong><br/>
            Contact: ${esc(client?.primary_contact_name || "")} · ${esc(client?.primary_contact_email || "")}<br/>
            Date: ${new Date().toLocaleDateString()} · Ref: ${p.id.slice(0, 8).toUpperCase()}
          </div>
        </div>
        <div class="brand-r">SoverenX · Network Integration Services<br/>Cisco · Fortinet · Palo Alto · F5 · Checkpoint</div>
      </div>

      <h2>Bill of Quantity</h2>
      <table>
        <thead><tr><th>#</th><th>Description</th><th style="text-align:right;">Qty</th><th style="text-align:right;">Unit</th><th style="text-align:right;">Total</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="totals">
        <div><span>Subtotal</span><span>$${liveTotals.subtotal.toFixed(2)}</span></div>
        <div><span>Discount</span><span>- $${liveTotals.global_discount.toFixed(2)}</span></div>
        <div><span>Tax</span><span>+ $${liveTotals.tax.toFixed(2)}</span></div>
        <div class="grand"><span>Grand Total</span><span>$${liveTotals.grand_total.toFixed(2)}</span></div>
      </div>

      <div class="page-break"></div>
      ${sowHtml}

      <div style="margin-top:60px;color:#888;font-size:11px;">Terms: Prices valid 30 days from date of issue. Payment terms: net 30. Signed acceptance required to commence.</div>
      <script>window.onload = () => { setTimeout(() => window.print(), 400); }<\/script>
      </body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  };

  return (
    <div>
      <button onClick={onBack} data-testid="proposal-back-btn" className="flex items-center gap-1 text-xs font-mono uppercase text-slate-500 hover:text-[#0F1E38] mb-4" style={{transition:"color 0.15s ease"}}><ArrowLeft size={14}/> All proposals</button>

      <div className="flex items-start justify-between mb-6 pb-6 border-b border-slate-200">
        <div>
          <div className="overline">{client?.company_name || "—"}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">{p.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${STATUS_STYLE[p.status]}`}>{p.status}</span>
            <VendorTag vendor={p.intake?.vendor_preference} />
            <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-slate-300 text-slate-500 uppercase">{p.intake?.topology}</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-slate-300 text-slate-500">{p.intake?.num_sites} sites</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="proposal-pdf-btn" onClick={exportPDF} className="bg-[#FFC107] hover:bg-[#E5A906] font-mono uppercase text-xs tracking-wider"><FilePdf size={14} className="mr-2"/> Export PDF</Button>
          {p.status !== "approved" && p.status !== "signed" && (
            <Button
              data-testid="approve-proposal-btn"
              onClick={approveProposal}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-mono uppercase text-xs tracking-wider"
            >
              <PaperPlaneTilt size={14} className="mr-2"/> Approve Proposal
            </Button>
          )}
          {p.project_id && (
            <a href={`/pipeline`} className="inline-flex items-center px-3 py-2 h-9 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs font-mono uppercase hover:bg-emerald-100">
              <Kanban size={14} className="mr-1.5"/> View Project
            </a>
          )}
          <Select value={p.status} onValueChange={setStatus}>
            <SelectTrigger data-testid="proposal-status" className="w-40 bg-transparent border-slate-300 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              {!isLocked && <SelectItem value="draft">Draft</SelectItem>}
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="awaiting_signature">Awaiting Signature</SelectItem>
              <SelectItem value="signed">Signed</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={onDelete} variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash size={14}/></Button>
        </div>
      </div>

      {/* Client Review Link — visible whenever the proposal is approved and
          not yet signed. Auto-populated on Approve (or on load for previously
          approved proposals). Admin copies this URL into their Outlook email
          along with the SOW PDF export — no separate "Generate Link" click. */}
      {signing?.signing_url && isLocked && p.status !== "signed" && (
        <div data-testid="signing-link-banner" className="border border-emerald-300 bg-emerald-50 rounded-lg p-5 mb-6">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="overline text-emerald-800">Client Review Link</div>
              <div className="text-[11px] text-emerald-900/80 mt-1">
                Send this URL to the customer alongside the SOW PDF. When they open it and click <b>Accept</b>, the project auto-advances to <b>Signed → Won</b>.
              </div>
            </div>
            <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 bg-white h-fit whitespace-nowrap">
              {signing.signing_expires_at ? `Expires ${new Date(signing.signing_expires_at).toLocaleDateString()}` : "Active"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={signing.signing_url}
              data-testid="signing-link-url"
              className="font-mono text-xs bg-white border-emerald-300 flex-1"
              onFocus={(e) => e.target.select()}
            />
            <Button
              data-testid="signing-link-copy"
              onClick={async () => {
                try { await navigator.clipboard.writeText(signing.signing_url); toast.success("Link copied"); }
                catch { toast.error("Copy failed — select and copy manually"); }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-mono uppercase text-xs tracking-wider h-9"
            >
              Copy Link
            </Button>
            <a
              href={signing.signing_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="signing-link-open"
              className="inline-flex items-center px-3 py-2 h-9 rounded border border-emerald-400 bg-white text-emerald-700 text-xs font-mono uppercase hover:bg-emerald-100"
            >
              Preview
            </a>
            <Button
              data-testid="signing-link-pdf"
              onClick={exportPDF}
              className="bg-[#FFC107] hover:bg-[#E5A906] text-slate-900 font-mono uppercase text-xs tracking-wider h-9"
            >
              <FilePdf size={14} className="mr-2"/> SOW PDF
            </Button>
          </div>
        </div>
      )}

      {/* Intake summary */}
      <div className="border border-slate-200 bg-white rounded-lg p-5 mb-6">
        <div className="overline mb-3">Client Requirements</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <Kv k="Topology" v={p.intake?.topology}/>
          <Kv k="Sites" v={p.intake?.num_sites}/>
          <Kv k="Preferred Vendor" v={p.intake?.vendor_preference}/>
          <Kv k="Timeline" v={p.intake?.timeline}/>
          <Kv k="Budget" v={p.intake?.budget_range}/>
          <Kv k="Contact" v={p.intake?.contact_name}/>
          <Kv k="Email" v={p.intake?.contact_email} mono/>
          <Kv k="Phone" v={p.intake?.contact_phone} mono/>
        </div>
        {p.intake?.features_required?.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Features</div>
            <div className="flex flex-wrap gap-1">{p.intake.features_required.map(f => <span key={f} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">{f}</span>)}</div>
          </div>
        )}
        {p.intake?.technical_requirements && (
          <div className="mt-4">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Technical Notes</div>
            <div className="text-xs text-slate-700 whitespace-pre-wrap">{p.intake.technical_requirements}</div>
          </div>
        )}
      </div>

      {/* BOM — CC-editable BOM lines from PSDoc (source=psdoc) + admin add-ons (source=cc-added) */}
      <div className="border border-slate-200 bg-white rounded-lg p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Bill of Materials — from PSDoc · {isLocked ? "locked (proposal " + p.status + ")" : "fully editable on CC"}</div>
          {isLocked ? (
            <span data-testid="boq-locked-badge" title="Proposal is locked. Use a Change Order to amend BoM or commercial terms." className="text-[10px] font-mono uppercase px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-700">🔒 Locked · use Change Order</span>
          ) : !editingBoq ? (
            <div className="flex gap-2">
              <Button
                data-testid="add-sku-btn"
                onClick={() => { setEditingBoq(true); setItems([...items, { description: "", sku: "", qty: 1, unit_price: 0, discount_pct: 0, vendor: "", source: "cc-added" }]); }}
                size="sm"
                className="h-8 text-[11px] font-mono uppercase bg-[#FFC107] hover:bg-[#E5A906] text-slate-900"
              >
                <Plus size={12} className="mr-1"/> Add SKU
              </Button>
              <Button data-testid="edit-boq-btn" onClick={() => setEditingBoq(true)} size="sm" className="h-8 text-[11px] font-mono uppercase bg-slate-100 hover:bg-slate-200 text-[#0F1E38] border border-slate-300"><PencilSimple size={11} className="mr-1"/> Edit BoM</Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => { setEditingBoq(false); setItems(normalize(bomSource)); setCt(initCT); }} variant="outline" className="h-7 text-[10px] font-mono uppercase border-slate-300 bg-transparent">Cancel</Button>
              <Button data-testid="save-boq-btn" size="sm" onClick={saveBoq} className="h-7 text-[10px] font-mono uppercase bg-[#FFC107] hover:bg-[#E5A906]">Save</Button>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left overline border-b border-slate-200">
                <th className="pb-2">Source</th><th>Description</th><th>SKU</th><th>Vendor</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Disc%</th><th className="text-right">Net</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const net = (Number(it.qty) || 0) * (Number(it.unit_price) || 0) * (1 - (Number(it.discount_pct) || 0) / 100);
                return (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2">
                      <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${it.source === "psdoc" ? "text-blue-700 bg-blue-50 border-blue-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
                        {it.source === "psdoc" ? "PSDoc" : "CC added"}
                      </span>
                    </td>
                    {editingBoq ? <>
                      <td><Input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} className="bg-transparent border-slate-300 text-xs h-8"/></td>
                      <td><Input value={it.sku} onChange={(e) => updateItem(i, { sku: e.target.value })} className="bg-transparent border-slate-300 text-xs h-8 font-mono w-28"/></td>
                      <td><Input value={it.vendor} onChange={(e) => updateItem(i, { vendor: e.target.value })} className="bg-transparent border-slate-300 text-xs h-8 w-24"/></td>
                      <td className="text-right"><Input type="number" value={it.qty} onChange={(e) => updateItem(i, { qty: parseFloat(e.target.value) || 0 })} className="bg-transparent border-slate-300 text-xs h-8 font-mono w-16 text-right"/></td>
                      <td className="text-right"><Input type="number" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: parseFloat(e.target.value) || 0 })} className="bg-transparent border-slate-300 text-xs h-8 font-mono w-24 text-right"/></td>
                      <td className="text-right"><Input type="number" value={it.discount_pct} onChange={(e) => updateItem(i, { discount_pct: parseFloat(e.target.value) || 0 })} className="bg-transparent border-slate-300 text-xs h-8 font-mono w-16 text-right"/></td>
                      <td className="text-right font-mono text-xs">{fmtCurrency(net)}</td>
                      <td><button onClick={() => removeItem(i)} data-testid={`remove-bom-line-${i}`} className="text-slate-500 hover:text-red-400"><X size={14}/></button></td>
                    </> : <>
                      <td className="py-2 text-slate-800">{it.description}</td>
                      <td className="font-mono text-[11px] text-slate-500">{it.sku}</td>
                      <td className="text-xs text-slate-600">{it.vendor}</td>
                      <td className="text-right font-mono">{it.qty}</td>
                      <td className="text-right font-mono">
                        {fmtCurrency(it.unit_price)}
                        {it.price_source === "unpriced" && !isLocked && (
                          <button
                            data-testid={`add-to-catalog-${i}`}
                            title="Add this SKU to the CC catalog so future SOWs auto-price"
                            onClick={() => setCatalogAdd({
                              sku: it.sku, description: it.description, vendor: it.vendor,
                              list_price: "", targetIndex: i,
                            })}
                            className="ml-1 text-[9px] font-mono uppercase text-amber-700 hover:text-amber-900 hover:underline"
                          >
                            + catalog
                          </button>
                        )}
                      </td>
                      <td className="text-right font-mono text-slate-500">{it.discount_pct}%</td>
                      <td className="text-right font-mono text-[#0F1E38]">{fmtCurrency(net)}</td>
                      <td></td>
                    </>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {editingBoq && (
          <div className="mt-3">
            <Button size="sm" onClick={addItem} data-testid="add-bom-line" className="h-8 text-xs bg-[#FFC107] hover:bg-[#E5A906]"><Plus size={12} className="mr-1"/> Add Line (CC-added)</Button>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-slate-200 flex justify-end">
          <div className="w-72 space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-500"><span>Subtotal</span><span className="font-mono">{fmtCurrency(liveTotals.subtotal)}</span></div>
            <div className="flex justify-between text-slate-500"><span>Global Discount ({ct.global_discount_pct}%)</span><span className="font-mono">- {fmtCurrency(liveTotals.global_discount)}</span></div>
            <div className="flex justify-between text-slate-500"><span>Tax ({ct.global_tax_pct}%)</span><span className="font-mono">+ {fmtCurrency(liveTotals.tax)}</span></div>
            <div className="flex justify-between pt-2 border-t border-slate-200"><span className="text-[#0F1E38] font-medium">Grand Total</span><span data-testid="proposal-grand-total" className="font-mono font-bold text-xl text-[#0F1E38]">{fmtCurrency(liveTotals.grand_total)}</span></div>
          </div>
        </div>
      </div>

      {/* Commercial Terms — CC-only */}
      <div className="border border-slate-200 bg-white rounded-lg p-5 mb-6" data-testid="commercial-terms-section">
        <div className="overline mb-3">Commercial Terms — CC-editable</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Global Discount %</label>
            <Input type="number" value={ct.global_discount_pct} onChange={(e) => setCt({ ...ct, global_discount_pct: parseFloat(e.target.value) || 0 })} className="bg-slate-50 border-slate-300 h-8 font-mono text-xs mt-1" data-testid="ct-global-disc"/>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Global Tax %</label>
            <Input type="number" value={ct.global_tax_pct} onChange={(e) => setCt({ ...ct, global_tax_pct: parseFloat(e.target.value) || 0 })} className="bg-slate-50 border-slate-300 h-8 font-mono text-xs mt-1" data-testid="ct-global-tax"/>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Payment Terms</label>
            <Input value={ct.payment_terms} onChange={(e) => setCt({ ...ct, payment_terms: e.target.value })} className="bg-slate-50 border-slate-300 h-8 text-xs mt-1" data-testid="ct-payment-terms"/>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Deposit %</label>
            <Input type="number" value={ct.deposit_pct} onChange={(e) => setCt({ ...ct, deposit_pct: parseFloat(e.target.value) || 0 })} className="bg-slate-50 border-slate-300 h-8 font-mono text-xs mt-1" data-testid="ct-deposit"/>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Currency</label>
            <Input value={ct.currency} onChange={(e) => setCt({ ...ct, currency: e.target.value })} className="bg-slate-50 border-slate-300 h-8 text-xs mt-1" data-testid="ct-currency"/>
          </div>
          <div className="col-span-2 md:col-span-3">
            <label className="text-[10px] font-mono uppercase text-slate-500">Sales / Negotiation Notes</label>
            <Textarea rows={2} value={ct.sales_notes} onChange={(e) => setCt({ ...ct, sales_notes: e.target.value })} className="bg-slate-50 border-slate-300 text-xs mt-1" data-testid="ct-sales-notes"/>
          </div>
        </div>
        {!editingBoq && (
          <p className="text-[11px] text-slate-500 italic mt-3">Click <b>Edit</b> on the BOM section above to modify these — Save writes both together.</p>
        )}
      </div>

      {/* Add-to-catalog mini-dialog — populated by clicking "+ catalog" on an unpriced BoM line */}
      {catalogAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="add-to-catalog-dialog">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4">
            <div>
              <div className="text-sm font-semibold text-[#0F1E38]">Add to CC catalog</div>
              <div className="text-xs text-slate-500 mt-1">Once saved, every future SOW that ships this SKU will auto-price. Case + whitespace don't matter — CC normalises the SKU key.</div>
            </div>
            <div className="space-y-2 text-sm">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">SKU</label>
                <Input value={catalogAdd.sku} onChange={(e) => setCatalogAdd({...catalogAdd, sku: e.target.value})} className="font-mono h-9" data-testid="add-catalog-sku"/>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Description</label>
                <Input value={catalogAdd.description} onChange={(e) => setCatalogAdd({...catalogAdd, description: e.target.value})} className="h-9" data-testid="add-catalog-desc"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Vendor</label>
                  <Input value={catalogAdd.vendor} onChange={(e) => setCatalogAdd({...catalogAdd, vendor: e.target.value})} placeholder="Fortinet, Cisco…" className="h-9"/>
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">List Price (AUD)</label>
                  <Input type="number" value={catalogAdd.list_price} onChange={(e) => setCatalogAdd({...catalogAdd, list_price: e.target.value})} placeholder="0.00" className="h-9 font-mono" data-testid="add-catalog-price"/>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={() => setCatalogAdd(null)} className="border-slate-300 bg-transparent h-9">Cancel</Button>
              <Button
                size="sm"
                data-testid="add-catalog-save"
                onClick={async () => {
                  const price = parseFloat(catalogAdd.list_price);
                  if (!catalogAdd.sku.trim()) { toast.error("SKU required"); return; }
                  if (!price || price <= 0) { toast.error("Enter a list price greater than 0"); return; }
                  try {
                    // Existing /devices contract — see server.py POST /devices.
                    await api.post("/devices", {
                      sku: catalogAdd.sku.trim(),
                      vendor: catalogAdd.vendor || "",
                      model: catalogAdd.description || catalogAdd.sku,
                      category: "hardware",
                      list_price: price,
                      labor_days: 0,
                    });
                    // Reflect the new price on THIS proposal's BoM line immediately —
                    // no need to wait for the next sow_ready. The Save BoM step
                    // will persist it (or admin can Approve as-is).
                    setItems((prev) => prev.map((it, idx) =>
                      idx === catalogAdd.targetIndex
                        ? { ...it, unit_price: price, price_source: "cc_catalog" }
                        : it
                    ));
                    setCatalogAdd(null);
                    toast.success(`Added ${catalogAdd.sku} to catalog — line re-priced to ${fmtCurrency(price)}`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to add SKU to catalog");
                  }
                }}
                className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] h-9"
              >Save to Catalog</Button>
            </div>
          </div>
        </div>
      )}

      <div className="border border-slate-200 bg-white rounded-lg p-6" data-testid="sow-readonly-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Sparkle size={14} weight="fill" className="text-[#0F1E38]" /><div className="overline">Statement of Work — from PSDoc (read-only)</div></div>
          <div className="flex gap-2">
            {p.hld_url && <a href={p.hld_url} target="_blank" rel="noreferrer" className="text-[10px] font-mono uppercase px-2 py-1 border border-slate-300 rounded text-slate-700 hover:bg-slate-50">HLD</a>}
            {p.lld_url && <a href={p.lld_url} target="_blank" rel="noreferrer" className="text-[10px] font-mono uppercase px-2 py-1 border border-slate-300 rounded text-slate-700 hover:bg-slate-50">LLD</a>}
            {p.diagram_url && <a href={p.diagram_url} target="_blank" rel="noreferrer" className="text-[10px] font-mono uppercase px-2 py-1 border border-slate-300 rounded text-slate-700 hover:bg-slate-50">Diagram</a>}
          </div>
        </div>
        <div data-testid="proposal-sow" className="text-sm text-slate-800 space-y-4">
          {sowSource.solution_overview && (
            <div><div className="overline mb-1">Solution Overview</div><p className="whitespace-pre-wrap">{sowSource.solution_overview}</p></div>
          )}
          {Array.isArray(sowSource.scope) && sowSource.scope.length > 0 && (
            <div><div className="overline mb-1">Scope</div><ul className="list-disc list-inside space-y-1">{sowSource.scope.map((s, i) => <li key={i}>{typeof s === "string" ? s : JSON.stringify(s)}</li>)}</ul></div>
          )}
          {Array.isArray(sowSource.deliverables) && sowSource.deliverables.length > 0 && (
            <div><div className="overline mb-1">Deliverables</div><ul className="list-disc list-inside space-y-1">{sowSource.deliverables.map((s, i) => <li key={i}>{typeof s === "string" ? s : JSON.stringify(s)}</li>)}</ul></div>
          )}
          {Array.isArray(sowSource.assumptions) && sowSource.assumptions.length > 0 && (
            <div><div className="overline mb-1">Assumptions</div><ul className="list-disc list-inside space-y-1">{sowSource.assumptions.map((s, i) => <li key={i}>{typeof s === "string" ? s : JSON.stringify(s)}</li>)}</ul></div>
          )}
          {Array.isArray(sowSource.out_of_scope) && sowSource.out_of_scope.length > 0 && (
            <div><div className="overline mb-1">Out of Scope</div><ul className="list-disc list-inside space-y-1">{sowSource.out_of_scope.map((s, i) => <li key={i}>{typeof s === "string" ? s : JSON.stringify(s)}</li>)}</ul></div>
          )}
          {Array.isArray(sowSource.dependencies) && sowSource.dependencies.length > 0 && (
            <div><div className="overline mb-1">Dependencies</div><ul className="list-disc list-inside space-y-1">{sowSource.dependencies.map((s, i) => <li key={i}>{typeof s === "string" ? s : JSON.stringify(s)}</li>)}</ul></div>
          )}
          {(!sowSource.solution_overview && !Array.isArray(sowSource.scope)) && (
            p.sow_markdown ? <MarkdownLite text={p.sow_markdown} /> : <p className="text-slate-500 italic text-xs">Waiting for PSDoc to submit the SOW.</p>
          )}
        </div>
      </div>

      {/* Scope Requests — the intermediate state between customer ask and CO */}
      {["approved", "signed", "awaiting_signature"].includes(p.status) && (
        <ScopeRequestsSection proposal={p} onConvertedToCo={() => onUpdate({ ...p, _refresh: Date.now() })} />
      )}

      {/* Change Orders — Feb 2026. Only visible on approved/signed proposals. */}
      {["approved", "signed", "awaiting_signature"].includes(p.status) && (
        <ChangeOrdersSection proposal={p} onChange={() => onUpdate({ ...p, _refresh: Date.now() })} />
      )}
    </div>
  );
}

// ============================================================================
// Scope Requests — customer asks logged before they graduate to a Change Order.
function ScopeRequestsSection({ proposal, onConvertedToCo }) {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ description: "", raised_by: "", notes: "" });
  const load = async () => {
    try { const r = await api.get(`/proposals/${proposal.id}/scope-requests`); setRows(r.data || []); }
    catch { /* fine — empty */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [proposal.id]);

  const save = async () => {
    if (!draft.description.trim()) { toast.error("Describe the request"); return; }
    try {
      await api.post(`/proposals/${proposal.id}/scope-requests`, draft);
      setDraft({ description: "", raised_by: "", notes: "" }); setAdding(false); load();
      toast.success("Scope request logged");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const patch = async (id, body) => {
    try { await api.patch(`/scope-requests/${id}`, body); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this scope request?")) return;
    try { await api.delete(`/scope-requests/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const convert = async (id) => {
    if (!window.confirm("Create a scope_change Change Order pre-filled from this request?")) return;
    try {
      const r = await api.post(`/scope-requests/${id}/convert-to-co`);
      toast.success(`CO ${r.data.co_id.slice(0,8)}… drafted from this request`);
      load(); onConvertedToCo && onConvertedToCo();
    } catch (e) { toast.error(e.response?.data?.detail || "Convert failed"); }
  };

  const STATUS_TONE = {
    pending:    "bg-amber-50 text-amber-700 border-amber-200",
    in_scope:   "bg-emerald-50 text-emerald-700 border-emerald-200",
    needs_co:   "bg-blue-50 text-blue-700 border-blue-200",
    dismissed:  "bg-slate-100 text-slate-600 border-slate-300",
  };

  return (
    <div className="border border-slate-200 bg-white rounded-lg p-6 mt-4" data-testid="scope-requests-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Scope Requests</div>
          <div className="text-[11px] text-slate-500 mt-1">Log every customer ask after the SOW is locked. Triage into: already covered, dismissed, or convert to a Change Order.</div>
        </div>
        {!adding && <Button size="sm" onClick={() => setAdding(true)} data-testid="add-scope-request-btn" className="h-7 text-[10px] font-mono uppercase bg-slate-100 hover:bg-slate-200 text-[#0F1E38] border border-slate-300"><Plus size={11} className="mr-1"/>Log Request</Button>}
      </div>
      {adding && (
        <div className="border border-slate-200 rounded p-3 space-y-2 mb-3 bg-slate-50">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Request description *</label>
            <Textarea rows={2} value={draft.description} onChange={(e) => setDraft({...draft, description: e.target.value})} placeholder="e.g. Customer wants wireless at Site 5" className="bg-white" data-testid="scope-request-desc"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Raised by</label>
              <Input value={draft.raised_by} onChange={(e) => setDraft({...draft, raised_by: e.target.value})} placeholder="Jane @ ACME" className="h-9 bg-white"/>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Internal notes</label>
              <Input value={draft.notes} onChange={(e) => setDraft({...draft, notes: e.target.value})} placeholder="Optional context" className="h-9 bg-white"/>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => { setAdding(false); setDraft({description:"",raised_by:"",notes:""}); }} className="h-8 border-slate-300 bg-transparent">Cancel</Button>
            <Button size="sm" onClick={save} data-testid="save-scope-request-btn" className="h-8 bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38]">Log Request</Button>
          </div>
        </div>
      )}
      {rows.length === 0 && !adding && (
        <div className="text-[11px] italic text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded">No scope requests logged yet.</div>
      )}
      {rows.map((r) => (
        <div key={r.id} data-testid={`scope-request-${r.id}`} className="border border-slate-200 rounded p-3 mb-2 flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${STATUS_TONE[r.status]}`}>{r.status.replace("_"," ")}</span>
              {r.raised_by && <span className="text-[10px] text-slate-500 font-mono">raised by {r.raised_by}</span>}
              <span className="text-[10px] text-slate-400 font-mono">{new Date(r.created_at).toLocaleString()}</span>
              {r.linked_co_id && <span className="text-[10px] text-blue-600 font-mono">→ CO {r.linked_co_id.slice(0,8)}…</span>}
            </div>
            <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{r.description}</div>
            {r.resolution_note && <div className="text-[11px] text-slate-500 italic mt-1">Resolution: {r.resolution_note}</div>}
            {r.notes && <div className="text-[11px] text-slate-500 mt-1">Notes: {r.notes}</div>}
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            {r.status === "pending" && (
              <>
                <Button size="sm" onClick={() => {
                  const note = window.prompt("Which SOW section covers this? (optional note)") || "";
                  patch(r.id, { status: "in_scope", resolution_note: note });
                }} data-testid={`mark-in-scope-${r.id}`} className="h-7 text-[10px] font-mono uppercase bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200">In Scope</Button>
                <Button size="sm" onClick={() => convert(r.id)} data-testid={`convert-to-co-${r.id}`} className="h-7 text-[10px] font-mono uppercase bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200">→ Create CO</Button>
                <Button size="sm" onClick={() => {
                  const note = window.prompt("Why is this dismissed?") || "";
                  patch(r.id, { status: "dismissed", resolution_note: note });
                }} className="h-7 text-[10px] font-mono uppercase bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-300">Dismiss</Button>
              </>
            )}
            <button onClick={() => del(r.id)} title="Delete" className="text-slate-400 hover:text-red-500 text-xs mt-1"><Trash size={12}/></button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Change Orders — collapsible section on the Proposal detail view.
// ============================================================================
function ChangeOrdersSection({ proposal, onChange }) {
  const [cos, setCos] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", reason: "", flavour: "commercial_only", bom_delta: { added: [], removed: [], modified: [] }, commercial_delta: {}, timeline_delta_days: 0 });
  const [newLine, setNewLine] = useState({ sku: "", description: "", qty: 1, unit_price: 0, discount_pct: 0, vendor: "" });

  const load = async () => {
    try {
      const r = await api.get(`/proposals/${proposal.id}/change-orders`);
      setCos(r.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [proposal.id]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      const r = await api.post(`/proposals/${proposal.id}/change-orders`, form);
      toast.success(`Created CO-${String(r.data.co_seq).padStart(3, "0")}`);
      setCreating(false);
      setForm({ title: "", reason: "", flavour: "commercial_only", bom_delta: { added: [], removed: [], modified: [] }, commercial_delta: {}, timeline_delta_days: 0 });
      load();
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };
  const sendToPsdoc = async (co) => {
    try { await api.post(`/change-orders/${co.id}/send-to-psdoc`); toast.success("Sent to PSDoc for engineering review"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const approve = async (co) => {
    try { await api.post(`/change-orders/${co.id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const sendSignature = async (co) => {
    try {
      const r = await api.post(`/change-orders/${co.id}/send-signature`);
      const link = `${window.location.origin}${r.data.sign_url}`;
      await navigator.clipboard.writeText(link);
      toast.success("Sign link copied — paste into Outlook and send to customer");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const markSigned = async (co) => {
    if (!window.confirm("Confirm the customer has signed and returned CO-" + String(co.co_seq).padStart(3, "0") + "?")) return;
    try { await api.post(`/change-orders/${co.id}/mark-signed`); toast.success("Signed — milestone auto-created"); load(); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addLineToDraft = (kind) => {
    if (!newLine.description) { toast.error("Description required"); return; }
    setForm({ ...form, bom_delta: { ...form.bom_delta, [kind]: [...form.bom_delta[kind], { ...newLine }] } });
    setNewLine({ sku: "", description: "", qty: 1, unit_price: 0, discount_pct: 0, vendor: "" });
  };

  const statusStyle = {
    draft:               "text-slate-600 bg-slate-100 border-slate-300",
    engineering_review:  "text-blue-700 bg-blue-50 border-blue-200",
    ack_received:        "text-emerald-700 bg-emerald-50 border-emerald-200",
    approved:            "text-violet-700 bg-violet-50 border-violet-200",
    awaiting_signature:  "text-amber-700 bg-amber-50 border-amber-200",
    signed:              "text-emerald-800 bg-emerald-100 border-emerald-300",
    bounced:             "text-red-700 bg-red-50 border-red-200",
  };

  return (
    <div className="border border-slate-200 bg-white rounded-lg p-5 mt-6" data-testid="change-orders-section">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Change Orders · {cos.length} · {cos.filter(c => c.status === "signed").length} signed</div>
        <Button data-testid="new-co-btn" onClick={() => setCreating(true)} size="sm" className="h-7 text-[10px] font-mono uppercase bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38]"><Plus size={11} className="mr-1"/> New Change Order</Button>
      </div>

      {cos.length === 0 && !creating && (
        <p className="text-xs text-slate-500 italic">No change orders yet. Create one if the customer requests a mid-project scope or commercial change.</p>
      )}

      {cos.map((co) => (
        <div key={co.id} data-testid={`co-${co.co_seq}`} className="border border-slate-200 rounded p-3 mb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-display font-bold text-sm text-[#0F1E38]">CO-{String(co.co_seq).padStart(3, "0")} · {co.title}</div>
              <div className="text-[10px] font-mono uppercase text-slate-500 mt-0.5">{co.flavour.replace("_", " ")} · impact {fmtCurrency(co.grand_total_delta || 0)}</div>
            </div>
            <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded border ${statusStyle[co.status] || statusStyle.draft}`}>{co.status.replace(/_/g, " ")}</span>
          </div>
          {co.reason && <p className="text-xs text-slate-600 mt-2">{co.reason}</p>}
          {co.engineering_note && <p className="text-xs text-slate-700 mt-2 italic bg-blue-50 border border-blue-100 rounded p-2">PSDoc: "{co.engineering_note}"</p>}
          {co.bounce_reason && <p className="text-xs text-red-700 mt-2 italic bg-red-50 border border-red-200 rounded p-2">Bounced: {co.bounce_reason}</p>}
          <div className="flex gap-2 mt-3 flex-wrap">
            {co.hld_delta_url && <a href={co.hld_delta_url} target="_blank" rel="noreferrer" className="text-[10px] font-mono uppercase px-2 py-1 border border-slate-300 rounded text-slate-700 hover:bg-slate-50">HLD Δ</a>}
            {co.lld_delta_url && <a href={co.lld_delta_url} target="_blank" rel="noreferrer" className="text-[10px] font-mono uppercase px-2 py-1 border border-slate-300 rounded text-slate-700 hover:bg-slate-50">LLD Δ</a>}
            {co.diagram_delta_url && <a href={co.diagram_delta_url} target="_blank" rel="noreferrer" className="text-[10px] font-mono uppercase px-2 py-1 border border-slate-300 rounded text-slate-700 hover:bg-slate-50">Diagram Δ</a>}
          </div>
          <div className="flex gap-2 mt-3">
            {co.status === "draft" && co.flavour === "scope_change" && (
              <Button size="sm" onClick={() => sendToPsdoc(co)} data-testid={`co-send-psdoc-${co.co_seq}`} className="h-7 text-[10px] font-mono uppercase bg-blue-600 hover:bg-blue-700 text-white">Send to PSDoc</Button>
            )}
            {co.status === "ack_received" && (
              <Button size="sm" onClick={() => approve(co)} data-testid={`co-approve-${co.co_seq}`} className="h-7 text-[10px] font-mono uppercase bg-emerald-600 hover:bg-emerald-700 text-white">Approve</Button>
            )}
            {co.status === "approved" && (
              <Button size="sm" onClick={() => sendSignature(co)} data-testid={`co-send-sign-${co.co_seq}`} className="h-7 text-[10px] font-mono uppercase bg-amber-500 hover:bg-amber-600 text-white">Copy Sign Link</Button>
            )}
            {(co.status === "awaiting_signature" || co.status === "approved") && (
              <Button size="sm" onClick={() => markSigned(co)} data-testid={`co-mark-signed-${co.co_seq}`} variant="outline" className="h-7 text-[10px] font-mono uppercase border-emerald-300 text-emerald-700 bg-transparent">Mark Signed</Button>
            )}
          </div>
        </div>
      ))}

      {creating && (
        <div className="border-2 border-amber-300 bg-amber-50/40 rounded p-4 mt-2" data-testid="co-create-form">
          <div className="overline mb-3 text-amber-800">New Change Order</div>
          <div className="space-y-3">
            <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Add 2 sites in QLD" className="bg-white border-slate-300 h-9 text-sm" data-testid="co-title"/></div>
            <div><Label className="text-xs">Reason</Label><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why the customer requested this change" className="bg-white border-slate-300 text-sm" data-testid="co-reason"/></div>
            <div>
              <Label className="text-xs">Type</Label>
              <div className="flex gap-2 mt-1">
                <button type="button" onClick={() => setForm({ ...form, flavour: "commercial_only" })} data-testid="co-flavour-commercial" className={`px-3 py-2 rounded border-2 text-xs ${form.flavour === "commercial_only" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-600"}`}>Commercial only (no tech impact)</button>
                <button type="button" onClick={() => setForm({ ...form, flavour: "scope_change" })} data-testid="co-flavour-scope" className={`px-3 py-2 rounded border-2 text-xs ${form.flavour === "scope_change" ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600"}`}>Scope change (requires PSDoc review)</button>
              </div>
            </div>
            {form.flavour === "scope_change" && (
              <div className="border border-slate-200 rounded p-3 bg-white">
                <Label className="text-xs">BOM lines to add</Label>
                <div className="grid grid-cols-6 gap-2 mt-2">
                  <Input placeholder="SKU" value={newLine.sku} onChange={(e) => setNewLine({ ...newLine, sku: e.target.value })} className="col-span-1 h-8 text-xs" data-testid="co-newline-sku"/>
                  <Input placeholder="Description" value={newLine.description} onChange={(e) => setNewLine({ ...newLine, description: e.target.value })} className="col-span-2 h-8 text-xs" data-testid="co-newline-desc"/>
                  <Input type="number" placeholder="Qty" value={newLine.qty} onChange={(e) => setNewLine({ ...newLine, qty: parseFloat(e.target.value) || 0 })} className="h-8 text-xs" data-testid="co-newline-qty"/>
                  <Input type="number" placeholder="Unit $" value={newLine.unit_price} onChange={(e) => setNewLine({ ...newLine, unit_price: parseFloat(e.target.value) || 0 })} className="h-8 text-xs" data-testid="co-newline-price"/>
                  <Button size="sm" onClick={() => addLineToDraft("added")} className="h-8 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="co-add-line-btn">+ Add</Button>
                </div>
                {form.bom_delta.added.length > 0 && (
                  <ul className="mt-2 text-xs space-y-1">
                    {form.bom_delta.added.map((l, i) => <li key={i} className="flex justify-between text-slate-700"><span>{l.qty}× {l.description} ({l.sku})</span><span className="font-mono">{fmtCurrency(l.qty * l.unit_price)}</span></li>)}
                  </ul>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs">Timeline delta (days)</Label>
              <Input type="number" value={form.timeline_delta_days} onChange={(e) => setForm({ ...form, timeline_delta_days: parseInt(e.target.value) || 0 })} className="bg-white border-slate-300 h-9 text-sm w-24" data-testid="co-timeline"/>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button size="sm" variant="outline" onClick={() => setCreating(false)} className="h-8 text-[10px] font-mono uppercase border-slate-300 bg-transparent">Cancel</Button>
            <Button size="sm" onClick={create} data-testid="co-create-submit" className="h-8 text-[10px] font-mono uppercase bg-[#FFC107] hover:bg-[#E5A906]">Create Draft</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const Kv = ({ k, v, mono }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{k}</div>
    <div className={`text-[#0F1E38] mt-0.5 ${mono ? "font-mono text-xs" : ""}`}>{v || "—"}</div>
  </div>
);

function esc(s) { return (s || "").replace(/[&<>"']/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c])); }
function mdToHtml(md) {
  const lines = md.split("\n"); const out = []; let list = [];
  const flush = () => { if (list.length) { out.push("<ul>" + list.join("") + "</ul>"); list = []; } };
  lines.forEach((l) => {
    if (/^#{3}\s+/.test(l)) { flush(); out.push(`<h3>${esc(l.replace(/^###\s+/, ""))}</h3>`); }
    else if (/^##\s+/.test(l)) { flush(); out.push(`<h2>${esc(l.replace(/^##\s+/, ""))}</h2>`); }
    else if (/^#\s+/.test(l)) { flush(); out.push(`<h1>${esc(l.replace(/^#\s+/, ""))}</h1>`); }
    else if (/^[-*]\s+/.test(l)) { list.push(`<li>${inline(l.replace(/^[-*]\s+/, ""))}</li>`); }
    else if (l.trim() === "") { flush(); }
    else { flush(); out.push(`<p>${inline(l)}</p>`); }
  });
  flush();
  return out.join("\n");
}
function inline(s) { return esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/`(.+?)`/g, "<code>$1</code>"); }

function MarkdownLite({ text }) {
  const lines = text.split("\n"); const out = []; let list = [];
  const flush = () => { if (list.length) { out.push(<ul key={out.length} className="list-disc pl-6 space-y-1 my-2 text-slate-700">{list}</ul>); list = []; } };
  lines.forEach((l, i) => {
    if (/^#{3}\s+/.test(l)) { flush(); out.push(<h3 key={i} className="font-display text-base font-bold text-[#0F1E38] mt-5 mb-2">{l.replace(/^###\s+/, "")}</h3>); }
    else if (/^##\s+/.test(l)) { flush(); out.push(<h2 key={i} className="font-display text-lg font-bold text-[#0F1E38] mt-6 mb-2">{l.replace(/^##\s+/, "")}</h2>); }
    else if (/^#\s+/.test(l)) { flush(); out.push(<h1 key={i} className="font-display text-xl font-bold text-[#0F1E38] mt-6 mb-2">{l.replace(/^#\s+/, "")}</h1>); }
    else if (/^[-*]\s+/.test(l)) { list.push(<li key={i} dangerouslySetInnerHTML={{ __html: inlineFmt(l.replace(/^[-*]\s+/, "")) }} />); }
    else if (l.trim() === "") { flush(); }
    else { flush(); out.push(<p key={i} className="text-slate-700 my-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: inlineFmt(l) }} />); }
  });
  flush();
  return <>{out}</>;
}
function inlineFmt(s) { return s.replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#0F1E38]">$1</strong>').replace(/`(.+?)`/g, '<code class="font-mono text-[#0F1E38] bg-blue-500/10 px-1 rounded">$1</code>'); }
